import { useEffect, useRef, useState } from 'react';

const MASK_HEIGHT_PX = 56;
/** Ease factor per frame (~60fps); lower = smoother follow, higher = snappier. */
const FOLLOW_EASE = 0.22;

/**
 * Reading mask — dims the page except a horizontal band at the pointer (ADHD / focus).
 * Position updates run on rAF with eased follow (no per-move React re-renders).
 */
export function ReadingMaskOverlay() {
  const [active, setActive] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setActive(root.dataset.a11yReadingMask === 'on');
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['data-a11y-reading-mask'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;

    const el = overlayRef.current;
    if (!el) return;

    let targetY = window.innerHeight / 2;
    let currentY = targetY;
    let rafId = 0;

    const applyBand = (centerY: number) => {
      const vh = window.innerHeight || 1;
      const top = Math.max(0, centerY - MASK_HEIGHT_PX / 2);
      const bottom = Math.min(vh, top + MASK_HEIGHT_PX);
      el.style.setProperty('--reading-mask-top', `${(top / vh) * 100}%`);
      el.style.setProperty('--reading-mask-bottom', `${(bottom / vh) * 100}%`);
    };

    const onMove = (e: PointerEvent) => {
      targetY = e.clientY;
    };

    const onResize = () => {
      targetY = Math.min(targetY, window.innerHeight);
      currentY = Math.min(currentY, window.innerHeight);
      applyBand(currentY);
    };

    const tick = () => {
      const delta = targetY - currentY;
      if (Math.abs(delta) < 0.35) {
        currentY = targetY;
      } else {
        currentY += delta * FOLLOW_EASE;
      }
      applyBand(currentY);
      rafId = requestAnimationFrame(tick);
    };

    applyBand(currentY);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('resize', onResize);
    };
  }, [active]);

  if (!active) return null;

  return (
    <div
      ref={overlayRef}
      className="reading-mask-overlay pointer-events-none fixed inset-0 z-[250]"
      aria-hidden
    />
  );
}
